import React, {
  useState,
  useEffect,
  useRef,
  useCallback
} from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { speakWithPolly } from '../utils/pollyService';

function InterviewPage({
  questions,
  currentIndex,
  mode,
  selectedJob, // 🆕 selectedJob props 추가
  onNext,
  darkMode
}) {
  const [displayedText, setDisplayedText] = useState('');
  const [timer, setTimer] = useState(45);
  const [initialTime, setInitialTime] = useState(45);
  const [showTimer, setShowTimer] = useState(false);
  const [silenceCounter, setSilenceCounter] = useState(0);
  const [isAnswering, setIsAnswering] = useState(false);
  const [questionPhase, setQuestionPhase] = useState('준비중');
  // 🆕 마이크 권한 상태 추가
  const [micPermissionGranted, setMicPermissionGranted] = useState(false);

  const currentQuestionRef = useRef('');
  const timerRef = useRef(null);
  const typingRef = useRef(null);
  const silenceRef = useRef(null);
  const isInitializedRef = useRef(false);
  const isTransitioningRef = useRef(false);

  const { transcript, resetTranscript } = useSpeechRecognition();

  const cardClass = darkMode
    ? 'bg-gray-800 text-white'
    : 'bg-white text-gray-800';

  const removeParentheses = useCallback((text) => {
    return text
      .replace(/\([^)]*\)/g, '')
      // 실제 사용되는 약어들만 변환
      .replace(/AB테스트/g, '에이비테스트')
      .replace(/AB 테스트/g, '에이비 테스트')
      .replace(/API/g, '에이피아이')
      .replace(/CAD/g, '캐드')
      .replace(/CRM/g, '씨알엠')
      .replace(/ERP/g, '이알피')
      .replace(/GDPR/g, '지디피알')
      .replace(/HRD/g, '에이치알디')
      .replace(/HRM/g, '에이치알엠')
      .replace(/ISO/g, '아이에스오')
      .replace(/IT/g, '아이티')
      .replace(/KPI/g, '케이피아이')
      .replace(/MES/g, '엠이에스')
      .replace(/PCB/g, '피씨비')
      .replace(/RESTful/g, '레스트풀')
      .replace(/SOC/g, '에스오씨')
      .replace(/SPC/g, '에스피씨')
      .replace(/SQL/g, '에스큐엘')
      .replace(/UI/g, '유아이')
      .replace(/UX/g, '유엑스')
      // 새로 추가된 약어들
      .replace(/TCP/g, '티씨피')
      .replace(/UDP/g, '유디피')
      .replace(/EBITDA/g, '이비다')
      .replace(/EVA/g, '이브이에이')
      .replace(/Cash Flow/g, '캐시 플로우')
      .replace(/OP-AMP/g, '오피앰프')
      .replace(/AD/g, '에이디')
      .replace(/QA/g, '큐에이')
      .replace(/QC/g, '큐씨')
      .replace(/MD/g, '엠디')
      .replace(/QCD/g, '큐씨디')
      .replace(/RAID/g, '레이드')
      .replace(/KCL/g, '케이씨엘')
      .replace(/KVL/g, '케이브이엘')
      .replace(/EHS/g, '이에이치에스')
      .replace(/\s+/g, ' ')
      .trim();
  }, []);

  const typeText = useCallback((text) => {
    let i = 0;
    setDisplayedText('');
    clearInterval(typingRef.current);

    typingRef.current = setInterval(() => {
      if (i < text.length) {
        setDisplayedText(text.substring(0, i + 1));
        i++;
      } else {
        clearInterval(typingRef.current);
      }
    }, 50);
  }, []);

  const cleanupTimers = useCallback(() => {
    clearInterval(timerRef.current);
    clearInterval(typingRef.current);
    clearInterval(silenceRef.current);
    SpeechRecognition.stopListening();
  }, []);

  // 🆕 마이크 권한 요청 함수
  const requestMicrophonePermission = useCallback(() => {
    return new Promise((resolve, reject) => {
      console.log('🎤 마이크 권한 요청 시작');
      setQuestionPhase('마이크 권한 요청중');
      
      try {
        // 음성 인식을 짧게 시작해서 권한 요청 트리거
        SpeechRecognition.startListening({ continuous: false });
        
        // 1초 후 중지하고 권한이 허용되었는지 확인
        setTimeout(() => {
          SpeechRecognition.stopListening();
          
          // 권한이 허용되었다면 resolve
          console.log('🎤 마이크 권한 요청 완료');
          setMicPermissionGranted(true);
          resolve();
        }, 1000);
        
      } catch (error) {
        console.error('🎤 마이크 권한 요청 실패:', error);
        reject(error);
      }
    });
  }, []);

  const startAnswerTime = useCallback(() => {
    setQuestionPhase('답변중');
    setInitialTime(45);
    setTimer(45);
    setShowTimer(true);

    SpeechRecognition.startListening({ continuous: true });

    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleNext = useCallback(() => {
    // 중복 실행 방지
    if (isTransitioningRef.current) {
      return;
    }
    isTransitioningRef.current = true;
    
    cleanupTimers();
    currentQuestionRef.current = '';
    onNext(transcript);
    
    // 1초 후 다시 사용 가능하게
    setTimeout(() => {
      isTransitioningRef.current = false;
    }, 1000);
  }, [cleanupTimers, onNext, transcript]);

  // 🔥 핵심 수정: 마이크 권한 먼저, 그 다음 텍스트+음성 동시 시작
  const initializeQuestion = useCallback(async () => {
    const currentQuestion = questions[currentIndex];
    if (currentQuestionRef.current === currentQuestion) return;

    currentQuestionRef.current = currentQuestion;
    cleanupTimers();
    resetTranscript();
    setSilenceCounter(0);
    setIsAnswering(false);
    setShowTimer(false);

    const questionForSpeech = removeParentheses(currentQuestion);

    // 🔥 첫 번째 질문이고 아직 마이크 권한이 없다면
    if (currentIndex === 0 && !micPermissionGranted) {
      try {
        // 1단계: 마이크 권한 먼저 요청 (텍스트 표시 안 함!)
        setQuestionPhase('마이크 권한 요청중');
        await requestMicrophonePermission();
        
        // 2단계: 권한 허용 후 텍스트와 음성 동시 시작!
        console.log('✅ 마이크 권한 허용됨 - 이제 텍스트와 음성 동시 시작!');
        setQuestionPhase('질문중');
        typeText(currentQuestion);        // ← 여기서 텍스트 시작!
        speakWithPolly(questionForSpeech, () => {
          setTimeout(() => {
            startAnswerTime();
          }, 1000);
        }, mode, currentIndex, selectedJob);  // 🔧 selectedJob 추가
        
      } catch (error) {
        console.error('❌ 마이크 권한 실패:', error);
        // 권한 실패해도 텍스트와 음성 시도
        setQuestionPhase('질문중');
        typeText(currentQuestion);
        speakWithPolly(questionForSpeech, () => {
          setTimeout(() => {
            startAnswerTime();
          }, 1000);
        }, mode, currentIndex, selectedJob);  // 🔧 selectedJob 추가
      }
    } else {
      // 2번째 질문부터는 기존과 동일 (권한이 이미 있음)
      setQuestionPhase('질문중');
      typeText(currentQuestion);
      speakWithPolly(questionForSpeech, () => {
        setTimeout(() => {
          startAnswerTime();
        }, 1000);
      }, mode, currentIndex, selectedJob);  // 🔧 selectedJob 추가
    }
  }, [questions, currentIndex, resetTranscript, cleanupTimers, typeText, removeParentheses, startAnswerTime, micPermissionGranted, requestMicrophonePermission, mode, selectedJob]); // 🔧 selectedJob 의존성 추가

  useEffect(() => {
    if (!isInitializedRef.current && questions && questions[currentIndex]) {
      isInitializedRef.current = true;
      setTimeout(() => {
        initializeQuestion();
      }, 500);
    }
  }, [questions, currentIndex, initializeQuestion]);

  useEffect(() => {
    if (isInitializedRef.current && questions && questions[currentIndex]) {
      setTimeout(() => {
        initializeQuestion();
      }, 500);
    }
  }, [currentIndex, initializeQuestion, questions]);

  useEffect(() => {
    if (showTimer && questionPhase === '답변중') {
      clearInterval(silenceRef.current);
      silenceRef.current = setInterval(() => {
        if (transcript.trim() === '') {
          setSilenceCounter(prev => prev + 1);
          setIsAnswering(false);
        } else {
          setSilenceCounter(0);
          setIsAnswering(true);
        }
      }, 1000);
      return () => clearInterval(silenceRef.current);
    }
  }, [showTimer, questionPhase, transcript]);

  useEffect(() => {
    if (timer === 0 && showTimer) {
      // 중복 실행 방지
      if (!isTransitioningRef.current) {
        setTimeout(() => {
          handleNext();
        }, 1000);
      }
    }
  }, [timer, showTimer, handleNext]);

  useEffect(() => {
    return () => {
      cleanupTimers();
      isInitializedRef.current = false;
      currentQuestionRef.current = '';
      isTransitioningRef.current = false;
    };
  }, [cleanupTimers]);

  const handleExtendTime = useCallback((seconds) => {
    setTimer(prev => prev + seconds);
    setInitialTime(prev => prev + seconds);
  }, []);

  const getStatusMessage = useCallback(() => {
    switch (questionPhase) {
      case '준비중': return '📝 질문을 준비하고 있습니다...';
      case '마이크 권한 요청중': return '🎤 마이크 권한을 허용해주세요!';
      case '질문중': return '';
      case '답변중':
        if (transcript.trim() && isAnswering) return '✅ 답변 중입니다';
        if (silenceCounter >= 1) return '🎤 말씀해주세요!';
        return '⏰ 답변을 기다리고 있습니다';
      default: return '대기 중...';
    }
  }, [questionPhase, transcript, isAnswering, silenceCounter]);

  if (!questions || !questions[currentIndex]) {
    return (
      <div className="text-center p-8">
        <div className="text-lg">질문을 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl text-center space-y-6">
      <div className={`text-xl font-bold p-3 rounded-xl ${darkMode ? 'bg-gray-800 bg-opacity-70' : 'bg-white bg-opacity-70'}`}>
        질문 {currentIndex + 1} / {questions.length}
      </div>

      <div className={`text-xl md:text-2xl p-8 rounded-2xl shadow-2xl font-semibold min-h-[120px] flex items-center justify-center ${cardClass}`}>
        {displayedText}
      </div>

      {showTimer && questionPhase === '답변중' && (
        <div className={`text-lg font-medium p-4 rounded-xl ${timer <= 10 ? 'animate-pulse text-red-500' : ''} ${darkMode ? 'bg-gray-800 bg-opacity-70' : 'bg-white bg-opacity-70'}`}>
          ⏱️ 남은 시간: <span className="font-bold text-2xl">{timer}초</span>
          <div className="w-full bg-gray-200 rounded-full h-3 mt-2">
            <div
              className={`h-3 rounded-full transition-all duration-1000 ${
                timer <= 10 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                timer <= 20 ? 'bg-gradient-to-r from-orange-500 to-orange-600' :
                'bg-gradient-to-r from-green-500 to-green-600'
              }`}
              style={{ width: `${Math.min(100, (timer / initialTime) * 100)}%` }}
            ></div>
          </div>
        </div>
      )}

      {showTimer && questionPhase === '답변중' && (
        <div className="flex flex-wrap justify-center gap-3">
          {/* 홈 버튼 추가 */}
          <button 
            onClick={() => {
              if (window.confirm('면접을 중단하고 홈으로 돌아가시겠습니까?')) {
                window.location.href = '/';
              }
            }}
            className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-xl shadow-lg"
          >
            🏠 홈으로
          </button>
          
          <button onClick={() => handleExtendTime(15)} className="px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-gray-800 font-semibold rounded-xl shadow-lg">
            ⏰ +15초
          </button>
          <button onClick={() => handleExtendTime(30)} className="px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-gray-800 font-semibold rounded-xl shadow-lg">
            ⏰ +30초
          </button>
          <button onClick={handleNext} className="px-8 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl shadow-lg">
            {currentIndex === questions.length - 1 ? '✅ 면접 완료' : '➡️ 다음 질문'}
          </button>
        </div>
      )}

      <div className={`text-sm p-3 rounded-xl ${darkMode ? 'bg-gray-800 bg-opacity-50' : 'bg-white bg-opacity-50'}`}>
        {getStatusMessage()}
      </div>

      <div className="text-xs opacity-75 text-green-500">
        🔊 완전한 로컬 음성 시스템 사용중 | 괄호 안 영어는 음성에서 자동 제거
      </div>
    </div>
  );
}

export default InterviewPage;